import { Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { hasPermission, type Permission } from '@dcp/shared'
import { useProfile } from '@/hooks/useProfile'

/**
 * Props for the RequirePermission gate component.
 *
 * @property permission - Permission key required to render children. Checked
 *   against the current user's product_role via the shared `hasPermission`
 *   helper, which mirrors the SQL `has_permission()` matrix.
 * @property children - Content to render when the permission is granted.
 * @property fallback - Optional override for the denied state. Defaults to a
 *   redirect to `/settings/profile` — the only authenticated route that is
 *   never permission-gated, so legacy users with `product_role=NULL` cannot
 *   be trapped in a redirect loop.
 */
interface RequirePermissionProps {
  permission: Permission
  children: React.ReactNode
  fallback?: React.ReactNode
}

/**
 * Route-level guard that enforces product-role permissions on the UI.
 *
 * Three states:
 * - Loading: spinner while the profile query is in flight (matches the
 *   ProtectedRoute pattern used by `App.tsx`).
 * - Denied: render `fallback`, defaulting to a redirect to `/settings/profile`
 *   (always reachable for any authenticated user, even with null product_role).
 * - Allowed: render children as-is.
 *
 * Notes on null handling:
 * - A null/undefined `productRole` (e.g. legacy users that never went through
 *   the product_role backfill, or platform staff with `product_role=NULL`) is
 *   treated as denied by the shared `hasPermission` helper. System-level
 *   admin features remain reachable through the orthogonal
 *   `canAccessAdminPanel` check.
 *
 * Usage — wrap inside `<ProtectedRoute>` so the auth check runs first:
 * ```tsx
 * <ProtectedRoute>
 *   <RequirePermission permission="view_banking">
 *     <BankingDashboardPage />
 *   </RequirePermission>
 * </ProtectedRoute>
 * ```
 */
export function RequirePermission({
  permission,
  children,
  fallback,
}: RequirePermissionProps) {
  const { data: profile, isLoading } = useProfile()

  // Show a centered spinner while the profile query is in flight to avoid a
  // false-deny flash before the productRole is known.
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!hasPermission(profile?.productRole, permission)) {
    if (fallback !== undefined) {
      return <>{fallback}</>
    }
    return <Navigate to="/settings/profile" replace />
  }

  return <>{children}</>
}
